import React from 'react';
import {
	FlexRow,
	FlexItem,
	TextField,
	Label,
	FlexColumn,
	DropdownList,
	ButtonDropdownField,
	DateRangePicker,
	Text,
} from '@forecast-it/design-system';
import {useIntl} from 'react-intl';
import {HIDDEN_FEATURES} from '../../../constants';
import ColorsPicker, {ColorsPickerDesignSystemSizeWrapper} from '../../../components/colors_picker';
import {isFeatureHidden} from '../../../forecast-app/shared/util/CompanySetupUtil';

type ProjectMainInfoProps = {
	label: string;
	projectName: string;
	onProjectNameChange: (value: string) => void;
	onProjectNameBlur: () => void;
	invalidProjectName: boolean;
	projectNameErrorMessage: string;
	customProjectId: string;
	defaultCustomProjectId: string;
	onCustomProjectIdChange: (value: string) => void;
	onCustomProjectIdBlur: (value: string) => void;
	customProjectIdError: string;
	projectColor: string;
	onColorSelect: (color: string) => void;
	startDate: Date;
	endDate: Date;
	handleDateRangeChange: (startDate: Date, endDate: Date) => void;
	client: string;
	clientOptions: {label: string; value: string; cy: string}[];
	onClientChange: (client: {label: string; value: string}) => void;
	useDuplication: boolean;
	duplicateColor: boolean;
	isOldCompany: boolean;
	isProjectLocked: boolean;
	lastCompanyProjectId: number;
};

export function ProjectMainInfo(props: ProjectMainInfoProps): JSX.Element {
	const {formatMessage} = useIntl();
	return (
		<FlexColumn gap={'l'}>
			<FlexRow alignItems={'start'}>
				<TextField
					data-cy={'new-project-name'}
					label={props.label}
					onValueChange={props.onProjectNameChange}
					onBlur={props.onProjectNameBlur}
					value={props.projectName}
					placeholder={formatMessage({id: 'new_project_modal.project_name_placeholder'})}
					errorText={props.projectNameErrorMessage}
					maxLength={191}
				/>
			</FlexRow>
			<FlexColumn gap={'xs'}>
				<FlexRow>
					<FlexItem>
						<TextField
							data-cy={'new-project-customId'}
							label={formatMessage({id: 'new_project_modal.project_id'})}
							onValueChange={props.onCustomProjectIdChange}
							onBlur={props.onCustomProjectIdBlur}
							hasError={!!props.customProjectIdError}
							value={props.customProjectId}
							maxLength={6}
							placeholder={`P${props.lastCompanyProjectId + 1}`}
						/>
					</FlexItem>
					{props.useDuplication && props.duplicateColor ? null : (
						<FlexColumn>
							<Label>{formatMessage({id: 'common.color'})}</Label>
							<ColorsPickerDesignSystemSizeWrapper>
								<ColorsPicker
									value={props.projectColor}
									rectangle={true}
									onColorSelect={props.onColorSelect}
									projectLocked={undefined}
									usePortal={true}
									cy={'new-project-color'}
								/>
							</ColorsPickerDesignSystemSizeWrapper>
						</FlexColumn>
					)}
				</FlexRow>
				{props.customProjectIdError ? (
					<FlexRow>
						<Text color="error" noWrap data-cy={'new-project-customId-error'}>
							{props.customProjectIdError}
						</Text>
					</FlexRow>
				) : null}
			</FlexColumn>
			<FlexRow>
				<FlexItem>
					<DateRangePicker
						data-cy={'new-project-dates'}
						label={formatMessage({id: 'common.dates'})}
						disabled={props.isProjectLocked}
						startDate={props.startDate}
						endDate={props.endDate}
						onChange={props.handleDateRangeChange}
					/>
				</FlexItem>

				{!isFeatureHidden(HIDDEN_FEATURES.CLIENT) ? (
					<FlexColumn>
						<Label>{formatMessage({id: 'common.client'})}</Label>
						<DropdownList>
							<DropdownList.Trigger data-cy={'settings-project-stage'}>
								<ButtonDropdownField>
									<Text>{props.clientOptions.find(o => o.value === props.client)?.label || 'No Client'}</Text>
								</ButtonDropdownField>
							</DropdownList.Trigger>

							<DropdownList.Content align={'start'}>
								{props.clientOptions.map(client => (
									<DropdownList.Item
										key={client.value}
										data-cy={client.cy}
										onClick={() => props.onClientChange(client)}
									>
										<FlexRow>
											<Text>{client.label}</Text>
										</FlexRow>
									</DropdownList.Item>
								))}
							</DropdownList.Content>
						</DropdownList>
					</FlexColumn>
				) : null}
			</FlexRow>
		</FlexColumn>
	);
}
